/**
 * Surface Gallery — the hidden `/loan/:id/test` page.
 *
 * A catalogue of every card *surface* in the app: background, border, radius
 * and shadow. Contentless on purpose — the question this page answers is "what
 * paper does this app use, and which one should I reach for?", not "what goes
 * inside a card".
 *
 * NOT in the nav. Reach it by typing the URL:
 *   /loan/<loanId>/test
 *
 * Two kinds of specimen:
 *   • Component surfaces — the real component renders around an empty spacer,
 *     so what you see is what it actually ships.
 *   • Inline surfaces — chrome hand-written in a tab's `sx`. These are declared
 *     as an sx block and the spec list is derived from it, so the swatch and its
 *     documentation can't disagree.
 *
 * Add a specimen whenever a new surface treatment appears — that's what keeps
 * the vocabulary from quietly forking into thirty near-identical greys.
 */
import { InboxOutlined } from "@mui/icons-material";
import { Box, Button, Card, Chip, Paper, TextField } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useMemo, useState } from "react";

import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import Flx from "../../../../components/layout/Flx";
import SectionCard from "../../../../components/layout/SectionCard";
import SectionPanel from "../../../../components/primitives/panel/SectionPanel";
import Txt from "../../../../components/typography/Txt";
import CardPaper from "../../../../components/ui/CardPaper";
import InsetCard from "../../../../components/ui/InsetCard";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import { KpiTile } from "../../shared/tabLayout/LoanTabKpiStrip";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import {
  TAB_TOKENS as STD,
  TONE_STYLES,
} from "../../shared/tabLayout/tabTokens";
import GalleryItem from "./GalleryItem";
import SurfaceSwatch, { SwatchFill } from "./SurfaceSwatch";
import { teal } from "../../../../theme/colors";

/* ─────────────────────────────────────────────
   Theme constants — mirrored from generateMuiThemeDefaults.jsx
   ─────────────────────────────────────────────
   Repeated here as *documentation values* for the spec lists. The swatches in
   the "Theme baseline" section render real MUI <Card>/<Paper>, so the visuals
   come from the theme itself, not from these strings.
*/
const THEME = {
  SURFACE: "#FFFFFF",
  LINE: "#E2E8F0",
  LINE_SOFT: "#eef2f6",
  LINE_HOVER: "#cbd5e1",
  CANVAS: "#F8FAFC",
  CARD_RADIUS: "14px",
  CONTROL_RADIUS: "8px",
  CARD_SHADOW: "0 1px 3px rgba(15,23,42,.05)",
  MODAL_SHADOW: "0 8px 28px rgba(15,23,42,.16)",
};

const LoanDrilldownCardGalleryTab = () => {
  const [query, setQuery] = useState("");

  const sections = useMemo(
    () => [
      /* ═══════════════════════════════════════════
         0 — The standard. Start here.
         ═══════════════════════════════════════════ */
      {
        key: "standard",
        title: "Tab standard",
        blurb:
          "The Drilldown Tab Header Standard's own surfaces. New tabs use these; everything below is either what they're built from or what they replace.",
        items: [
          {
            name: "LoanSectionCard",
            path: "screens/LoanDrilldownScreen/shared/tabLayout/LoanSectionCard.js",
            description:
              "Tier 2 — the card every tab section lives in. The standard's card radius (10px) and its two-layer contact shadow. Tones fill and outline together, never one alone.",
            usedIn: ["Closed package tab", "Trade economics tab"],
            variants: "neutral · ok · warn · danger · brand",
            render: () => (
              <>
                <SurfaceSwatch
                  label="neutral (default)"
                  spec={[
                    ["background", STD.color.surface],
                    ["border", `1px solid ${STD.color.border}`],
                    ["radius", STD.radius.card],
                    ["shadow", STD.shadow.card],
                  ]}
                >
                  <LoanSectionCard>
                    <SwatchFill height={48} />
                  </LoanSectionCard>
                </SurfaceSwatch>
                {["ok", "warn", "danger"].map((tone) => (
                  <SurfaceSwatch
                    key={tone}
                    label={tone}
                    note="Status fill + matching line. Use sparingly."
                    spec={[
                      ["background", TONE_STYLES[tone].fill],
                      ["border", `1px solid ${TONE_STYLES[tone].line}`],
                      ["radius", STD.radius.card],
                      ["shadow", STD.shadow.card],
                    ]}
                  >
                    <LoanSectionCard tone={tone}>
                      <SwatchFill height={48} />
                    </LoanSectionCard>
                  </SurfaceSwatch>
                ))}
              </>
            ),
          },
          {
            name: "KpiTile",
            path: "screens/LoanDrilldownScreen/shared/tabLayout/LoanTabKpiStrip.js",
            description:
              "The standard's KPI tile. Bare on the page background — never wrapped in a card, which would re-create the nesting problem the two-tier pattern exists to avoid. No shadow: these sit on the canvas, not above it.",
            usedIn: ["Closed package tab", "Trade economics tab"],
            variants: "neutral · ok · warn · danger · brand",
            swatchMinWidth: 200,
            render: () => (
              <>
                <SurfaceSwatch
                  label="neutral"
                  spec={[
                    ["background", STD.color.surface],
                    ["border", `1px solid ${STD.color.border}`],
                    ["radius", STD.radius.sm],
                    ["shadow", "none"],
                  ]}
                >
                  <KpiTile label="Payments made" value={14} />
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="ok"
                  spec={[
                    ["background", TONE_STYLES.ok.fill],
                    ["border", `1px solid ${TONE_STYLES.ok.line}`],
                    ["radius", STD.radius.sm],
                    ["shadow", "none"],
                  ]}
                >
                  <KpiTile label="Package checks" value="6 of 6" tone="ok" />
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="danger"
                  spec={[
                    ["background", TONE_STYLES.danger.fill],
                    ["border", `1px solid ${TONE_STYLES.danger.line}`],
                    ["radius", STD.radius.sm],
                    ["shadow", "none"],
                  ]}
                >
                  <KpiTile
                    label="Mortgagee clause"
                    value="Not validated"
                    tone="danger"
                  />
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="empty value"
                  note="Missing data is an em dash — never 0, N/A, or blank."
                  spec={[
                    ["background", STD.color.surface],
                    ["border", `1px solid ${STD.color.border}`],
                    ["radius", STD.radius.sm],
                    ["value color", STD.color.muted],
                  ]}
                >
                  <KpiTile label="Asset status" value={null} />
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "Field well",
            path: "screens/LoanDrilldownScreen/shared/tabLayout/tabTokens.js → surfaceSunk",
            description:
              "The sunk surface for read-only figures nested inside a card. No shadow — a shadow inside a card reads as a mistake.",
            usedIn: ["Performance snapshot", "Mortgagee clause preview"],
            render: () => (
              <SurfaceSwatch
                label="sunk well"
                surfaceSx={{
                  background: STD.color.surfaceSunk,
                  border: `1px solid ${STD.color.border}`,
                  borderRadius: STD.radius.sm,
                  boxShadow: "none",
                }}
              />
            ),
          },
        ],
      },

      /* ═══════════════════════════════════════════
         1 — What the theme gives you for free
         ═══════════════════════════════════════════ */
      {
        key: "theme",
        title: "Theme baseline",
        blurb:
          "Every <Card> and <Paper> already wears these. Reach for a bare MUI Card before hand-rolling a surface — most of the inline chrome further down exists only because someone didn't.",
        items: [
          {
            name: "MuiCard (default)",
            path: "functions/generateMuiThemeDefaults.jsx → components.MuiCard",
            description:
              "The house card. A plain <Card> with no props already carries surface, hairline border, 14px radius and the standard soft shadow.",
            usedIn: [
              "TitledCard",
              "PrimaryContentCard",
              "TablePreviewCard",
              "JsonPreviewCard",
              "anything rendering a bare <Card>",
            ],
            render: () => (
              <>
                <SurfaceSwatch
                  label="default"
                  note="No props needed."
                  spec={[
                    ["background", THEME.SURFACE],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", THEME.CARD_RADIUS],
                    ["shadow", THEME.CARD_SHADOW],
                  ]}
                >
                  <Card sx={{ p: 0 }}>
                    <SwatchFill />
                  </Card>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label='variant="outlined"'
                  note="Same chrome — the variant only re-asserts the shadow."
                  spec={[
                    ["background", THEME.SURFACE],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", THEME.CARD_RADIUS],
                    ["shadow", THEME.CARD_SHADOW],
                  ]}
                >
                  <Card variant="outlined" sx={{ p: 0 }}>
                    <SwatchFill />
                  </Card>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label='variant="elevated"'
                  note="Lifted off the page — for surfaces that float above content."
                  spec={[
                    ["background", THEME.SURFACE],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", THEME.CARD_RADIUS],
                    ["shadow", "0 12px 24px #0000000a"],
                  ]}
                >
                  <Card variant="elevated" sx={{ p: 0 }}>
                    <SwatchFill />
                  </Card>
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "MuiPaper",
            path: "functions/generateMuiThemeDefaults.jsx → components.MuiPaper",
            description:
              "Paper defaults to elevation 0 with a hairline border and no radius of its own. Elevations 1 and 8 (menus, popovers, dialogs) get the modal shadow.",
            usedIn: ["SectionCard", "LabelValueCard", "menus / popovers / dialogs"],
            render: () => (
              <>
                <SurfaceSwatch
                  label="elevation 0 (default)"
                  spec={[
                    ["background", THEME.SURFACE],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", "0 — set it yourself"],
                    ["shadow", "none"],
                  ]}
                >
                  <Paper>
                    <SwatchFill />
                  </Paper>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="elevation 1 / 8"
                  note="Overlay surfaces: Menu, Popover, Select, Dialog."
                  spec={[
                    ["background", THEME.SURFACE],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", "0 — set it yourself"],
                    ["shadow", THEME.MODAL_SHADOW],
                  ]}
                >
                  <Paper elevation={1}>
                    <SwatchFill />
                  </Paper>
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "Page canvas",
            path: "functions/generateMuiThemeDefaults.jsx → CANVAS",
            description:
              "The background surfaces sit on. Worth knowing: a white card on the canvas reads as a card; a white card on white does not.",
            usedIn: ["dashboard background"],
            canvas: "white",
            render: () => (
              <>
                <SurfaceSwatch
                  label="canvas"
                  surfaceSx={{
                    background: THEME.CANVAS,
                    border: `1px solid ${THEME.LINE_SOFT}`,
                  }}
                />
                <SurfaceSwatch
                  label="hairline scale"
                  note="LINE / LINE_SOFT / LINE_HOVER — default, inner divider, hover."
                  spec={[
                    ["default", THEME.LINE],
                    ["soft", THEME.LINE_SOFT],
                    ["hover", THEME.LINE_HOVER],
                  ]}
                >
                  <Flx column gap={0}>
                    {[THEME.LINE, THEME.LINE_SOFT, THEME.LINE_HOVER].map((c) => (
                      <Box
                        key={c}
                        sx={{ height: 26, background: THEME.SURFACE, borderBottom: `1px solid ${c}` }}
                      />
                    ))}
                  </Flx>
                </SurfaceSwatch>
              </>
            ),
          },
        ],
      },

      /* ═══════════════════════════════════════════
         2 — Surfaces shipped as components
         ═══════════════════════════════════════════ */
      {
        key: "components",
        title: "Component surfaces",
        blurb:
          "Papers you get by importing something. These render the real component, so the swatch is the truth.",
        items: [
          {
            name: "CardPaper",
            path: "components/ui/CardPaper.jsx",
            description:
              "Surface-only component — it exists purely to own this chrome. The lightest of the card shadows, with a hover deepen and a selection ring for list items.",
            usedIn: ["PropertyCard", "PropertyCardCompact", "ApplicantCard"],
            render: () => (
              <>
                <SurfaceSwatch
                  label="default"
                  spec={[
                    ["background", "#fff"],
                    ["border", `1px solid ${grey[200]}`],
                    ["radius", "10px"],
                    ["shadow", "0 1px 2px rgba(16,24,40,0.04)"],
                  ]}
                >
                  <CardPaper>
                    <SwatchFill />
                  </CardPaper>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="interactive (hover it)"
                  note="Border and shadow both step up on hover."
                  spec={[
                    ["border", `1px solid ${grey[300]}`],
                    ["shadow", "0 4px 12px rgba(16,24,40,0.06)"],
                    ["transition", "border-color .15s, box-shadow .15s"],
                  ]}
                >
                  <CardPaper onClick={() => {}}>
                    <SwatchFill />
                  </CardPaper>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="selected"
                  note="Accent border plus a 3px glow ring."
                  spec={[
                    ["border", `1px solid ${teal[500]}`],
                    ["radius", "10px"],
                    [
                      "shadow",
                      `0 0 0 3px ${teal[500]}1f, 0 1px 2px rgba(16,24,40,0.04)`,
                    ],
                  ]}
                >
                  <CardPaper selected onClick={() => {}}>
                    <SwatchFill />
                  </CardPaper>
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "PrimaryContentCard",
            path: "_src_shared/components/layout/PrimaryContentCard.js",
            description:
              "The heaviest surface in the app — a Tailwind-style shadow-lg. Note the responsive behavior: below 1600px it drops radius and shadow entirely and goes full-bleed, so on a laptop this card has no visible paper at all.",
            usedIn: [
              "Documents",
              "Fees (redesign)",
              "Exceptions",
              "Orders",
              "Economics / Closed Package",
              "Wire & Funding",
            ],
            render: () => (
              <>
                <SurfaceSwatch
                  label="≥1600px (desktop)"
                  spec={[
                    ["background", "#fff (MuiCard)"],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", THEME.CARD_RADIUS],
                    [
                      "shadow",
                      "0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1)",
                    ],
                  ]}
                  surfaceSx={{
                    background: "#fff",
                    border: `1px solid ${THEME.LINE}`,
                    borderRadius: THEME.CARD_RADIUS,
                    boxShadow:
                      "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.1) 0px 4px 6px -4px",
                  }}
                />
                <SurfaceSwatch
                  label="≤1599px (laptop)"
                  note="Radius and shadow removed; the card fills the main area."
                  spec={[
                    ["background", "#fff"],
                    ["border", `1px solid ${THEME.LINE}`],
                    ["radius", "0"],
                    ["shadow", "none"],
                  ]}
                  surfaceSx={{
                    background: "#fff",
                    border: `1px solid ${THEME.LINE}`,
                    borderRadius: 0,
                    boxShadow: "none",
                  }}
                />
              </>
            ),
          },
          {
            name: "SectionPanel",
            path: "components/primitives/panel/SectionPanel.js",
            description:
              "The Panel design-system surface. No shadow at all — hierarchy comes from a true 0.5px hairline that renders crisp on retina. Four variants, each a different amount of chrome.",
            usedIn: ["Property Panel design system", "sidebar sections"],
            variants: "standard · alert · subtle · bare",
            render: () => (
              <>
                <SurfaceSwatch
                  label="standard"
                  spec={[
                    ["background", "#ffffff"],
                    ["border", "0.5px solid rgba(0,0,0,0.08)"],
                    ["radius", "12px"],
                    ["shadow", "none"],
                  ]}
                >
                  <SectionPanel
                    title=""
                    disableCollapse
                    headerDivider={false}
                    py={0}
                  >
                    <SwatchFill height={48} />
                  </SectionPanel>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="alert"
                  note="Danger surface — carries its own fill and border."
                  spec={[
                    ["background", "#fcebeb"],
                    ["border", "0.5px solid #f7c1c1"],
                    ["radius", "12px"],
                    ["shadow", "none"],
                  ]}
                >
                  <SectionPanel
                    title=""
                    variant="alert"
                    headerDivider={false}
                    py={0}
                  >
                    <SwatchFill height={48} />
                  </SectionPanel>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="subtle"
                  note="Borderless muted fill for de-emphasized groups."
                  spec={[
                    ["background", "#f1efe8"],
                    ["border", "none"],
                    ["radius", "12px"],
                    ["shadow", "none"],
                  ]}
                >
                  <SectionPanel
                    title=""
                    variant="subtle"
                    disableCollapse
                    headerDivider={false}
                    py={0}
                  >
                    <SwatchFill height={48} />
                  </SectionPanel>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="bare"
                  note="No paper at all — a section divider wearing a header."
                  spec={[
                    ["background", "transparent"],
                    ["border", "none"],
                    ["radius", "12px (no visible edge)"],
                    ["shadow", "none"],
                  ]}
                >
                  <SectionPanel
                    title=""
                    variant="bare"
                    disableCollapse
                    headerDivider={false}
                    py={0}
                  >
                    <SwatchFill height={48} />
                  </SectionPanel>
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "PanelCard",
            path: "components/sidebar/shared/PanelCard.js",
            description:
              "Same surface vocabulary as SectionPanel — it's the older copy, still mounted in the sidebars. Identical chrome; SectionPanel adds the bare variant and themed header bands.",
            usedIn: [
              "LoanBorrowerSidebar",
              "LoanSubjectPropertySidebar",
              "Borrower required-docs checklist",
            ],
            variants: "standard · alert · subtle",
            render: () => (
              <SurfaceSwatch
                label="standard"
                note="Alert and subtle match SectionPanel exactly."
                spec={[
                  ["background", "#ffffff"],
                  ["border", "0.5px solid rgba(0,0,0,0.08)"],
                  ["radius", "12px"],
                  ["shadow", "none"],
                ]}
                surfaceSx={{
                  background: "#ffffff",
                  border: "0.5px solid rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  boxShadow: "none",
                }}
              />
            ),
          },
          {
            name: "SectionCard",
            path: "components/layout/SectionCard.js",
            description:
              "Elevation-0 Paper with the widest radius in the app (sx borderRadius 3 → 24px, since the theme's shape unit is 8). Quiet enough to nest inside a form.",
            usedIn: ["Create/Update Condition form", "Condition sidebar (redesign)"],
            render: () => (
              <SurfaceSwatch
                label="default"
                spec={[
                  ["background", THEME.SURFACE],
                  ["border", "1px solid divider (#E2E8F0)"],
                  ["radius", "3 → 24px"],
                  ["shadow", "none"],
                ]}
              >
                <SectionCard sx={{ mb: 0, p: 0, py: 0 }}>
                  <SwatchFill />
                </SectionCard>
              </SurfaceSwatch>
            ),
          },
          {
            name: "InsetCard",
            path: "components/ui/InsetCard.js",
            description:
              "For nesting inside another card. No shadow — a shadow inside a card reads as a mistake. The `grey` variant fills instead.",
            usedIn: [
              "Fees & Closing sub-tabs",
              "Loan Terms tab",
              "Entity details card",
            ],
            render: () => (
              <>
                <SurfaceSwatch
                  label="default"
                  spec={[
                    ["background", "transparent"],
                    ["border", "1px solid #e5e7eb"],
                    ["radius", "2 → 16px"],
                    ["shadow", "none"],
                  ]}
                >
                  <InsetCard sx={{ py: 0, px: 0 }}>
                    <SwatchFill />
                  </InsetCard>
                </SurfaceSwatch>
                <SurfaceSwatch
                  label="grey"
                  spec={[
                    ["background", "#f9fafb"],
                    ["border", "1px solid #e5e7eb"],
                    ["radius", "2 → 16px"],
                    ["shadow", "none"],
                  ]}
                >
                  <InsetCard grey sx={{ py: 0, px: 0 }}>
                    <SwatchFill />
                  </InsetCard>
                </SurfaceSwatch>
              </>
            ),
          },
          {
            name: "KpiWidget",
            path: "components/data/KpiWidgetGroup.jsx",
            description:
              "Square corners on purpose — KPI tiles sit shoulder to shoulder in a grid, and radius on every tile reads as noise. Border color is the only thing the tone changes.",
            usedIn: [
              "Properties tab",
              "Applicants tab",
              "LoanBorrowerSidebar",
              "LoanSubjectPropertySidebar",
            ],
            render: () => (
              <>
                <SurfaceSwatch
                  label="neutral"
                  spec={[
                    ["background", "#ffffff"],
                    ["border", "1px solid #d8e0ea"],
                    ["radius", "0"],
                    ["shadow", "none"],
                  ]}
                  surfaceSx={{
                    background: "#ffffff",
                    border: "1px solid #d8e0ea",
                    borderRadius: 0,
                    boxShadow: "none",
                  }}
                />
                <SurfaceSwatch
                  label="toned (green / amber / red …)"
                  note="Tone tints the header band and border, never the body."
                  surfaceSx={{
                    background: "#ffffff",
                    border: "1px solid #a5d6a7",
                    borderRadius: 0,
                    boxShadow: "none",
                  }}
                />
              </>
            ),
          },
          {
            name: "HorizontalSummaryBar",
            path: "components/data/HorizontalSummaryBar.jsx",
            description:
              "A warmer paper than the rest of the app — the border and shadow come from the Key Dates palette (#e8e6df) rather than the slate scale.",
            usedIn: [],
            unused: true,
            render: () => (
              <SurfaceSwatch
                label="default"
                surfaceSx={{
                  background: "#ffffff",
                  border: "1px solid #e8e6df",
                  borderRadius: "8px",
                  boxShadow:
                    "0 1px 0 rgba(20,20,30,0.04), 0 1px 2px rgba(20,20,30,0.04)",
                }}
              />
            ),
          },
          {
            name: "TablePreviewCard",
            path: "components/ui/TablePreviewCard.js",
            description:
              "A theme Card with one addition: a 6px accent rail down the left edge, marking it as a drill-in preview rather than a section.",
            usedIn: [
              "Appraisal preview",
              "Exception preview",
              "Property drilldown",
            ],
            render: () => (
              <SurfaceSwatch
                label="default"
                spec={[
                  ["background", "#ffffff"],
                  ["border-left", "6px solid #eff7fe"],
                  ["radius", THEME.CARD_RADIUS],
                  ["shadow", THEME.CARD_SHADOW],
                ]}
                surfaceSx={{
                  background: "#ffffff",
                  border: `1px solid ${THEME.LINE}`,
                  borderLeftWidth: "6px",
                  borderLeftColor: "#eff7fe",
                  borderRadius: THEME.CARD_RADIUS,
                  boxShadow: THEME.CARD_SHADOW,
                }}
              />
            ),
          },
        ],
      },

      /* ═══════════════════════════════════════════
         3 — Inline surfaces written in tabs
         ═══════════════════════════════════════════ */
      {
        key: "inline",
        title: "Inline surfaces",
        blurb:
          "Chrome hand-written in a tab's sx rather than owned by a component. These are the candidates for consolidation — several are near-duplicates of each other.",
        items: [
          {
            name: "Elevated table shell",
            path: "tabs/LoanDrilldownSubjectPropertiesTab · LoanDrilldownApplicantsTab",
            description:
              "Wraps a document table. The two-layer shadow (tight contact + wide ambient) is the strongest non-modal elevation outside PrimaryContentCard.",
            usedIn: ["Properties files area", "Applicants files area"],
            render: () => (
              <SurfaceSwatch
                label="default"
                surfaceSx={{
                  background: "#fff",
                  border: "1px solid #d8e0ea",
                  borderRadius: "10px",
                  boxShadow:
                    "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)",
                }}
              />
            ),
          },
          {
            name: "AI panel card",
            path: "screens/LoanDrilldownScreen/components/AiReviewsPanel.js",
            description:
              "The outer surface of each AI Review panel. Same 14px radius as the theme card, but a lifted-ambient shadow with a negative spread so the glow sits under the card rather than around it.",
            usedIn: [
              "AI Reviews panel",
              "AI Findings panel",
              "Hard Exception panel",
              "Opening field writes panel",
            ],
            render: () => (
              <SurfaceSwatch
                label="panel"
                surfaceSx={{
                  background: "#fff",
                  border: "1px solid #e5e8ee",
                  borderRadius: "14px",
                  boxShadow:
                    "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
                }}
              />
            ),
          },
          {
            name: "AI agent row",
            path: "screens/LoanDrilldownScreen/components/AiReviewsPanel.js",
            description:
              "Rows nested inside the AI panel. The dashed border is a real state, not decoration — it marks an agent that hasn't run yet.",
            usedIn: ["AI Reviews panel"],
            render: () => (
              <>
                <SurfaceSwatch
                  label="ready"
                  surfaceSx={{
                    background: "#fff",
                    border: "1px solid #e5e8ee",
                    borderRadius: "10px",
                    boxShadow: "none",
                  }}
                />
                <SurfaceSwatch
                  label="pending / never run"
                  surfaceSx={{
                    background: "#fff",
                    border: "1px dashed #dbe1e8",
                    borderRadius: "10px",
                    boxShadow: "none",
                  }}
                />
                <SurfaceSwatch
                  label="hover"
                  surfaceSx={{
                    background: "#fff",
                    border: "1px solid #e5e8ee",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(20,30,45,0.05)",
                  }}
                />
              </>
            ),
          },
          {
            name: "Segmented switcher",
            path: "tabs/LoanDrilldownSubjectPropertiesTab → CardViewSwitcher",
            description:
              "The recessed pill track and its raised active thumb. The track border is required — without it the switcher floats next to bordered controls.",
            usedIn: [
              "Properties card-density switcher",
              "Fees Standard/Ledger toggle",
              "Summary view selector",
            ],
            render: () => (
              <>
                <SurfaceSwatch
                  label="track"
                  height={44}
                  surfaceSx={{
                    background: "#eef3f5",
                    border: "1px solid #e3e9ec",
                    borderRadius: "9px",
                    boxShadow: "none",
                  }}
                />
                <SurfaceSwatch
                  label="active thumb"
                  height={44}
                  surfaceSx={{
                    background: "#fff",
                    border: "none",
                    borderRadius: "7px",
                    boxShadow: "0 1px 3px rgba(22,36,43,.12)",
                  }}
                />
              </>
            ),
          },
          {
            name: "Focus / selection ring",
            path: "components/ui/CardPaper.jsx · various",
            description:
              "Selection is a ring, never a fill — a filled selected card changes its own contrast and breaks the row's rhythm.",
            usedIn: ["CardPaper (selected)", "keyboard focus states"],
            render: () => (
              <>
                <SurfaceSwatch
                  label="accent ring (3px)"
                  surfaceSx={{
                    background: "#fff",
                    border: `1px solid ${teal[500]}`,
                    borderRadius: "10px",
                    boxShadow: `0 0 0 3px ${teal[500]}1f`,
                  }}
                />
                <SurfaceSwatch
                  label="focus ring (2px)"
                  surfaceSx={{
                    background: "#fff",
                    border: `1px solid ${THEME.LINE}`,
                    borderRadius: "8px",
                    boxShadow: "0 0 0 2px rgba(59,130,246,0.4)",
                  }}
                />
              </>
            ),
          },
          {
            name: "Semantic fills",
            path: "components/primitives/panel/panelTokens.js · various",
            description:
              "Status surfaces. All are fill + matching border, no shadow — status is communicated by hue, and elevation on top of that reads as urgency it may not have.",
            usedIn: ["Alert panels", "condition/exception chips", "finding severity"],
            swatchMinWidth: 200,
            render: () => (
              <>
                <SurfaceSwatch
                  label="danger"
                  surfaceSx={{
                    background: "#fcebeb",
                    border: "1px solid #f7c1c1",
                    borderRadius: "8px",
                  }}
                />
                <SurfaceSwatch
                  label="info"
                  surfaceSx={{
                    background: "#e6f1fb",
                    border: "1px solid #bfdbfe",
                    borderRadius: "8px",
                  }}
                />
                <SurfaceSwatch
                  label="success"
                  surfaceSx={{
                    background: "#eaf3de",
                    border: "1px solid #a7f3d0",
                    borderRadius: "8px",
                  }}
                />
                <SurfaceSwatch
                  label="warning"
                  surfaceSx={{
                    background: "#fef3c7",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                  }}
                />
              </>
            ),
          },
        ],
      },
    ],
    [],
  );

  // Filter across name, path, description and usage, so "which surfaces does
  // the Fees tab use?" narrows the page the same way a name search does.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter((i) =>
          [i.name, i.path, i.description, ...(i.usedIn || [])]
            .join(" ")
            .toLowerCase()
            .includes(q),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [sections, query]);

  const totalCount = useMemo(
    () => sections.reduce((acc, s) => acc + s.items.length, 0),
    [sections],
  );
  const shownCount = useMemo(
    () => filtered.reduce((acc, s) => acc + s.items.length, 0),
    [filtered],
  );

  return (
    <LoanTabShell
      eyebrow="Internal · not in nav"
      title="Surface gallery"
      subTitle="Every card surface in the app — background, border, radius and shadow — with the components and tabs that use it."
      meta={
        <Chip
          label={`${totalCount} surfaces`}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
        />
      }
      endContent={
        <TextField
          size="small"
          value={query}
          placeholder="Filter by name, path or usage…"
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      }
      bodySx={{ gap: "40px" }}
    >
      {shownCount === 0 ? (
        <EmptyStatePlaceholder
          icon={<InboxOutlined />}
          title="No surfaces match that filter"
          description="Try a component name, a file path fragment, or the tab a surface is used on."
          actions={
            <Button size="small" onClick={() => setQuery("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        filtered.map((section) => (
          <Box key={section.key}>
            <Flx column gap={0.5} sx={{ mb: 2 }}>
              <Flx ac gap={1}>
                <Txt sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                  {section.title}
                </Txt>
                <Chip
                  label={section.items.length}
                  size="small"
                  sx={{ height: 19, fontSize: 11, fontWeight: 600 }}
                />
              </Flx>
              <Txt secondary sx={{ fontSize: 12.5 }}>
                {section.blurb}
              </Txt>
            </Flx>
            <Flx column gap={2.5}>
              {section.items.map((item) => (
                <GalleryItem
                  key={item.name}
                  name={item.name}
                  path={item.path}
                  description={item.description}
                  usedIn={item.usedIn}
                  variants={item.variants}
                  unused={item.unused}
                  canvas={item.canvas}
                  swatchMinWidth={item.swatchMinWidth}
                >
                  {item.render()}
                </GalleryItem>
              ))}
            </Flx>
          </Box>
        ))
      )}
    </LoanTabShell>
  );
};

export default LoanDrilldownCardGalleryTab;
